import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../App.css';
import { ChartConfig, AxisConfig, ColorConfig, LegendConfig, DataLabelConfig, SortConfig } from '../components/ChartRenderer';
import FilterableChart from '../components/FilterableChart';
import DownloadButton from '../components/DownloadButton';
import LocalFilesPanel from '../components/LocalFilesPanel';
import { useAuth } from '../context/AuthContext';
import { useDuckDBContext } from '../context/DuckDBContext';
import { useWebSocket } from '../hooks/useWebSocket';

// API base URL - uses environment variable in production, localhost in development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';


interface IterationInfo {
  iteration: number;
  action: string;
  detail: string;
  result?: string;
}

interface TokenUsage {
  input_tokens: number;
  output_tokens: number;
  total_input_tokens: number;
  total_output_tokens: number;
}

interface ProgressTask {
  id: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed';
}

interface StreamEvent {
  event_type: string;
  payload: {
    message?: string;
    query_id?: number | string;
    query_name?: string;
    data?: Record<string, unknown>[];
    row_count?: number;
    agent_item_id?: string;
    analysis?: string;
    error?: string;
    // Iteration fields
    iteration?: number;
    action?: string;
    detail?: string;
    result?: string;
    // Chart fields
    chart_type?: 'bar' | 'line' | 'pie' | 'tiles' | 'waffle';
    x_column?: string;
    y_column?: string;
    title?: string;
    x_label?: string;
    y_label?: string;
    // Chart config fields (from formatter)
    x_axis?: AxisConfig;
    y_axis?: AxisConfig;
    colors?: ColorConfig;
    legend?: LegendConfig;
    data_labels?: DataLabelConfig;
    sorting?: SortConfig;
    // Token fields
    input_tokens?: number;
    output_tokens?: number;
    total_input_tokens?: number;
    total_output_tokens?: number;
    // Formatting fields
    x_format?: string;
    y_format?: string;
    color_scheme?: string;
    show_legend?: boolean;
    show_data_labels?: boolean;
    // Local query fields
    sql?: string;
    // Fetch data fields (new architecture)
    table_name?: string;
    metadata?: {
      source: string;
      sourceQuery: string;
      question: string;
      rowCount: number;
      truncated: boolean;
      columns: { name: string; dataType: string; sampleValues: string[] }[];
      description: string;
    };
    warning?: string;
    // Explore query fields
    target_table?: string;
    // Progress tracking fields
    tasks?: ProgressTask[];
    // fetch_data_meta fields
    session_id?: string;
    columns?: { name: string; type: string }[];
    // Interactive filter fields
    filters?: { column: string; label: string }[];
    column_order?: string[];
    tile_formats?: Record<string, string>;
    source_table?: string;
    source_sql?: string;
    // Recon metadata for waffle reconciliation downloads
    recon_metadata?: {
      left_table: string;
      right_table: string;
      join_key: string;
      left_label?: string;
      right_label?: string;
    };
  };
}

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  data?: Record<string, unknown>[];
  rowCount?: number;
  isLoading?: boolean;
  status?: string;
  error?: string;
  iterations?: IterationInfo[];
  chartConfig?: ChartConfig;
  tokenUsage?: TokenUsage;
  sql?: string; // SQL query that generated this data (for saving to agent)
  agentItemId?: string; // If this message is from an agent item
  progressTasks?: ProgressTask[]; // Agent-driven task tracking
  queryId?: string; // Backend query record ID for feedback
  tableName?: string; // DuckDB table name for interactive filtering
  backendSessionId?: string; // Session ID for backend DuckDB queries
}

interface ChatSession {
  id: string;
  name: string;
  messages: ChatMessage[];
  activeTab: Record<string, ResultTab>;
  analysisName?: string; // If this session is running an agent
}

// Local schema interfaces (matching backend)
interface LocalColumnSchema {
  name: string;
  type: string;
  nullable: boolean;
}

interface LocalTableSchema {
  table_name: string;
  file_name: string;
  columns: LocalColumnSchema[];
}

type ResultTab = 'chart' | 'data' | 'sql' | 'analysis';

// Agent interfaces
interface AgentSummary {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  item_count: number;
}

type CommandDropdown = 'analysis' | null;

interface SaveToAnalysisData {
  messageId: string;
  sql: string;
  chartConfig: ChartConfig | null;
  suggestedName: string;
}

interface ChatPageProps {}

// Generate a short name from the first user message
function generateSessionName(message: string): string {
  // Take first 25 chars, trim at word boundary
  const trimmed = message.slice(0, 30).trim();
  const lastSpace = trimmed.lastIndexOf(' ');
  if (lastSpace > 15 && trimmed.length >= 30) {
    return trimmed.slice(0, lastSpace) + '...';
  }
  return trimmed.length >= 30 ? trimmed + '...' : trimmed;
}

// Convert DuckDB schemas to backend format
async function getLocalSchemasForBackend(
  getSchemas: () => Promise<{ tableName: string; columns: { name: string; type: string; nullable: boolean }[] }[]>,
  localFiles: { tableName: string; fileName: string }[]
): Promise<LocalTableSchema[] | undefined> {
  if (localFiles.length === 0) return undefined;

  try {
    const schemas = await getSchemas();
    return schemas.map(schema => {
      const file = localFiles.find(f => f.tableName === schema.tableName);
      return {
        table_name: schema.tableName,
        file_name: file?.fileName || schema.tableName,
        columns: schema.columns.map(col => ({
          name: col.name,
          type: col.type,
          nullable: col.nullable
        }))
      };
    });
  } catch (err) {
    console.error('Failed to get local schemas:', err);
    return undefined;
  }
}

function ChatPage(_props: ChatPageProps) {
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const { localFiles, getSchemas, executeQuery: executeDuckDBQuery, storeFetchedData, fetchedDatasets } = useDuckDBContext();
  const navigate = useNavigate();
  const { company } = useParams<{ company?: string }>();

  // All requests use cookies (authenticated via Google OAuth)
  const getFetchOptions = (): RequestInit => {
    return { credentials: 'include' };
  };

  const getFetchOptionsWithBody = (body: object): RequestInit => {
    return {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    };
  };

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionIndex, setActiveSessionIndex] = useState(0);
  const [inputMessage, setInputMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const emptyInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentMessageIdRef = useRef<string | null>(null);
  const initializedRef = useRef(false);

  // Agent state
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [showAnalysisModal, setShowAgentModal] = useState(false);
  const [editedSql, setEditedSql] = useState<Record<string, string>>({});
  const [saveToAnalysisData, setSaveToAnalysisData] = useState<SaveToAnalysisData | null>(null);

  // Edit visualization state
  const [editVizPrompt, setEditVizPrompt] = useState<Record<string, string>>({});
  const [editVizLoading, setEditVizLoading] = useState<Record<string, boolean>>({});
  const [editVizPreview, setEditVizPreview] = useState<Record<string, { chartConfig: ChartConfig; data: Record<string, unknown>[]; } | null>>({});

  // Command dropdown state
  const [activeDropdown, setActiveDropdown] = useState<CommandDropdown>(null);

  // Handle logout and navigate to home
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Feedback state - tracks which messages have been rated
  const [feedbackGiven, setFeedbackGiven] = useState<Record<string, 'up' | 'down'>>({});

  // Get current session
  const currentSession = sessions[activeSessionIndex];
  const sessionId = currentSession?.id || null;
  const messages = currentSession?.messages || [];
  const activeTab = currentSession?.activeTab || {};

  // WebSocket connection for bi-directional communication
  const { sendDataStored } = useWebSocket(sessionId);

  // Helper to update current session
  const updateCurrentSession = (updater: (session: ChatSession) => ChatSession) => {
    setSessions(prev => prev.map((s, i) => i === activeSessionIndex ? updater(s) : s));
  };

  // Helper to set activeTab for current session
  const setActiveTab = (updater: (prev: Record<string, ResultTab>) => Record<string, ResultTab>) => {
    updateCurrentSession(s => ({ ...s, activeTab: updater(s.activeTab) }));
  };

  // Helper to set messages for current session
  const setMessages = (updater: (prev: ChatMessage[]) => ChatMessage[]) => {
    updateCurrentSession(s => ({ ...s, messages: updater(s.messages) }));
  };

  // Focus the appropriate input based on whether we have messages
  const focusInput = () => {
    // Small delay to ensure DOM is updated before focusing
    setTimeout(() => {
      if (messages.length === 0) {
        emptyInputRef.current?.focus();
      } else {
        inputRef.current?.focus();
      }
    }, 50);
  };

  // Fetch agents list
  const fetchAgents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/analysis`, getFetchOptions());
      if (response.ok) {
        const data = await response.json();
        setAgents(data.filter((a: AgentSummary) => a.name !== 'saved_queries'));
      }
    } catch (err) {
      console.error('Failed to fetch analyses:', err);
    }
  };

  // Auto-scroll to bottom when messages change or content updates
  useEffect(() => {
    // Small delay to ensure content is rendered before scrolling
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [messages]); // Trigger on any message change (including content updates)

  // Create initial session on mount once authenticated
  useEffect(() => {
    if (!initializedRef.current && isAuthenticated) {
      initializedRef.current = true;
      createNewSession();
      fetchAgents();
      setTimeout(() => emptyInputRef.current?.focus(), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Handle escape key to cancel request
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        console.log('[ESC] Escape pressed, isProcessing:', isProcessing, 'hasAbortController:', !!abortControllerRef.current);

        if (abortControllerRef.current) {
          console.log('[ESC] Aborting request...');
          abortControllerRef.current.abort();
          abortControllerRef.current = null;

          // Update the message to show cancelled state (search all sessions
          // since agent invocations may target a different session)
          if (currentMessageIdRef.current) {
            const cancelId = currentMessageIdRef.current + '-assistant';
            setSessions(prev => prev.map(s => ({
              ...s,
              messages: s.messages.map(m =>
                m.id === cancelId
                  ? { ...m, isLoading: false, status: undefined, content: 'Request cancelled' }
                  : m
              ),
            })));
          }

          setIsProcessing(false);
          focusInput();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing]);

  const createNewSession = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/session`, {
        method: 'POST',
        ...getFetchOptions(),
      });
      if (response.ok) {
        const data = await response.json();
        const newSession: ChatSession = {
          id: data.session_id,
          name: 'New Chat',
          messages: [],
          activeTab: {},
        };
        setSessions(prev => [...prev, newSession]);
        setActiveSessionIndex(prev => prev === 0 && sessions.length === 0 ? 0 : sessions.length);
        console.log('Session created:', data.session_id);
      }
    } catch (err) {
      console.error('Failed to create session:', err);
    }
  };

  const handleNewChat = async () => {
    await createNewSession();
    setInputMessage('');
    focusInput();
  };

  const handleCloseSession = async (index: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't switch to the tab when closing

    const sessionToClose = sessions[index];

    // Delete session on backend
    if (sessionToClose) {
      try {
        await fetch(`${API_BASE_URL}/session/${sessionToClose.id}`, {
          method: 'DELETE',
          ...getFetchOptions(),
        });
      } catch (err) {
        console.error('Failed to delete session:', err);
      }
    }

    // Remove from sessions array
    setSessions(prev => prev.filter((_, i) => i !== index));

    // Adjust active index
    if (sessions.length === 1) {
      // Last session, create a new one
      await createNewSession();
    } else if (index <= activeSessionIndex) {
      // Closed tab was before or at current, shift index back
      setActiveSessionIndex(prev => Math.max(0, prev - 1));
    }
  };

  const handleSwitchSession = (index: number) => {
    if (index !== activeSessionIndex && !isProcessing) {
      setActiveSessionIndex(index);
      focusInput();
    }
  };

  // Handle agent invocation from dropdown
  const handleAgentInvoke = async (analysisName: string) => {
    if (isProcessing) return;

    const messageId = Date.now().toString();

    // Find the agent to get display name
    const agent = agents.find(a => a.name === analysisName);
    const displayName = agent?.display_name || analysisName;

    // Check if current session has existing messages - if so, create a new session
    // Also create a new session if we don't have a valid session ID
    let targetSessionId = sessionId;
    if (messages.length > 0 || !sessionId) {
      // Create a new session for the agent
      try {
        const sessionResponse = await fetch(`${API_BASE_URL}/session`, {
          method: 'POST',
          ...getFetchOptions(),
        });
        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          targetSessionId = sessionData.session_id;
          const newSession: ChatSession = {
            id: sessionData.session_id,
            name: `/${displayName}`,
            messages: [],
            activeTab: {},
            analysisName: analysisName,
          };
          // If current session is empty and has no ID, replace it; otherwise add new
          if (messages.length === 0 && !sessionId) {
            setSessions(prev => {
              const updated = [...prev];
              updated[activeSessionIndex] = newSession;
              return updated;
            });
          } else {
            setSessions(prev => [...prev, newSession]);
            setActiveSessionIndex(sessions.length); // Switch to new session
          }
        }
      } catch (err) {
        console.error('Failed to create session for analysis:', err);
        return;
      }
    } else {
      // Use current empty session - update it with agent info
      updateCurrentSession(s => ({ ...s, name: `/${displayName}`, analysisName: analysisName }));
    }

    // Use a function to update messages in the target session
    const updateTargetMessages = (updater: (prev: ChatMessage[]) => ChatMessage[]) => {
      setSessions(prev => prev.map(s =>
        s.id === targetSessionId ? { ...s, messages: updater(s.messages) } : s
      ));
    };

    const updateTargetActiveTab = (updater: (prev: Record<string, ResultTab>) => Record<string, ResultTab>) => {
      setSessions(prev => prev.map(s =>
        s.id === targetSessionId ? { ...s, activeTab: updater(s.activeTab) } : s
      ));
    };

    // Add user message showing agent invocation
    updateTargetMessages(prev => [...prev, {
      id: messageId + '-user',
      type: 'user',
      content: `/${analysisName}`,
    }]);

    // Add assistant placeholder for status updates
    updateTargetMessages(prev => [...prev, {
      id: messageId + '-assistant',
      type: 'assistant',
      content: '',
      isLoading: true,
      status: `Running analysis: ${displayName}...`,
    }]);

    setIsProcessing(true);
    currentMessageIdRef.current = messageId;

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const fetchOpts = getFetchOptionsWithBody({
        message: '',
        session_id: targetSessionId,
        analysis: analysisName,
      });
      const response = await fetch(`${API_BASE_URL}/chat/stream`, {
        method: 'POST',
        ...fetchOpts,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error('Failed to run analysis');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      let accumulatedAnalysis = '';
      let accumulatedTokenUsage: { input_tokens: number; output_tokens: number; total_input_tokens: number; total_output_tokens: number } | undefined;
      // Track current item being processed (data comes before chart)
      let currentItemData: Record<string, unknown>[] | undefined;
      let currentItemName: string | undefined;
      let currentItemIndex: number = 0;
      let currentAgentItemId: string | undefined;
      let currentItemSql: string | undefined;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data:')) {
            const jsonStr = line.startsWith('data: ') ? line.slice(6) : line.slice(5);
            if (jsonStr.trim()) {
              try {
                const event: StreamEvent = JSON.parse(jsonStr);

                console.log('[Agent] Event received:', event.event_type, event.payload);

                switch (event.event_type) {
                  case 'status':
                    updateTargetMessages(prev => prev.map(m =>
                      m.id === messageId + '-assistant'
                        ? { ...m, status: event.payload.message || 'Processing...' }
                        : m
                    ));
                    break;
                  case 'data':
                    // Store data for the current item - will be paired with chart event
                    currentItemData = event.payload.data;
                    currentItemName = event.payload.query_name;
                    currentItemIndex = Number(event.payload.query_id) || 0;
                    currentAgentItemId = event.payload.agent_item_id;
                    currentItemSql = event.payload.sql;
                    console.log('[Agent] Data stored:', currentItemName, currentItemData?.length, 'rows, itemId:', currentAgentItemId);
                    break;
                  case 'chart':
                    console.log('[Agent] Chart event - has data:', !!currentItemData, 'chart_type:', event.payload.chart_type, 'sorting:', event.payload.sorting);
                    // Chart event follows data event - create a new message with both
                    if (currentItemData && event.payload.chart_type) {
                      const itemMessageId = `${messageId}-item-${currentItemIndex}`;
                      // Capture values before closure to avoid stale references
                      const capturedData = currentItemData;
                      const capturedName = currentItemName;
                      const capturedAgentItemId = currentAgentItemId;
                      const capturedSql = currentItemSql;
                      const chartConfig: ChartConfig = {
                        chart_type: event.payload.chart_type,
                        x_column: event.payload.x_column || '',
                        y_column: event.payload.y_column || '',
                        title: event.payload.title || capturedName,
                        x_label: event.payload.x_label,
                        y_label: event.payload.y_label,
                        x_axis: event.payload.x_axis,
                        y_axis: event.payload.y_axis,
                        colors: event.payload.colors,
                        legend: event.payload.legend,
                        data_labels: event.payload.data_labels,
                        sorting: event.payload.sorting,
                        column_order: event.payload.column_order,
                        tile_formats: event.payload.tile_formats,
                        filters: event.payload.filters,
                        source_table: event.payload.source_table,
                        source_sql: event.payload.source_sql,
                        recon_metadata: event.payload.recon_metadata,
                      };
                      // Add a new message for this agent item with chart
                      updateTargetMessages(prev => {
                        // Insert before the status message
                        const statusIdx = prev.findIndex(m => m.id === messageId + '-assistant');
                        const newMessage: ChatMessage = {
                          id: itemMessageId,
                          type: 'assistant',
                          content: capturedName || 'Analysis Result',
                          data: capturedData,
                          rowCount: capturedData?.length || 0,
                          chartConfig: chartConfig,
                          agentItemId: capturedAgentItemId,
                          sql: capturedSql,
                        };
                        if (statusIdx >= 0) {
                          return [...prev.slice(0, statusIdx), newMessage, ...prev.slice(statusIdx)];
                        }
                        return [...prev, newMessage];
                      });
                      // Set chart tab as active for this item
                      updateTargetActiveTab(prev => ({ ...prev, [itemMessageId]: 'chart' }));
                      // Reset for next item
                      currentItemData = undefined;
                      currentItemName = undefined;
                      currentAgentItemId = undefined;
                      currentItemSql = undefined;
                    }
                    break;
                  case 'tokens':
                    accumulatedTokenUsage = {
                      input_tokens: event.payload.input_tokens || 0,
                      output_tokens: event.payload.output_tokens || 0,
                      total_input_tokens: event.payload.total_input_tokens || 0,
                      total_output_tokens: event.payload.total_output_tokens || 0,
                    };
                    break;
                  case 'analysis':
                    accumulatedAnalysis = event.payload.analysis || '';
                    break;
                  case 'fetch_data_meta':
                    // Metadata only — data stays in backend DuckDB (skill data loading)
                    if (event.payload.table_name) {
                      console.log(`[Agent] Table '${event.payload.table_name}' available in backend DuckDB (${event.payload.row_count || 0} rows)`);
                    }
                    break;
                  case 'error':
                    updateTargetMessages(prev => prev.map(m =>
                      m.id === messageId + '-assistant'
                        ? { ...m, isLoading: false, error: event.payload.error, status: undefined }
                        : m
                    ));
                    break;
                  case 'complete':
                    // Capture values before closures
                    const finalData = currentItemData;
                    const finalName = currentItemName;
                    const finalAnalysis = accumulatedAnalysis;
                    const finalItemSql = currentItemSql;
                    const finalTokenUsage1 = accumulatedTokenUsage;
                    // If there's leftover data without a chart, add it as a data-only message
                    if (finalData) {
                      const itemMessageId = `${messageId}-item-${currentItemIndex}`;
                      updateTargetMessages(prev => {
                        const statusIdx = prev.findIndex(m => m.id === messageId + '-assistant');
                        const newMessage: ChatMessage = {
                          id: itemMessageId,
                          type: 'assistant',
                          content: finalName || 'Analysis Result',
                          data: finalData,
                          rowCount: finalData?.length || 0,
                          sql: finalItemSql,
                        };
                        if (statusIdx >= 0) {
                          return [...prev.slice(0, statusIdx), newMessage, ...prev.slice(statusIdx)];
                        }
                        return [...prev, newMessage];
                      });
                      updateTargetActiveTab(prev => ({ ...prev, [itemMessageId]: 'data' }));
                    }
                    // Update the status message to show completion summary
                    const finalQueryId1 = event.payload.query_id ? String(event.payload.query_id) : undefined;
                    updateTargetMessages(prev => prev.map(m =>
                      m.id === messageId + '-assistant'
                        ? {
                            ...m,
                            isLoading: false,
                            status: undefined,
                            content: finalAnalysis,
                            queryId: finalQueryId1,
                            tokenUsage: finalTokenUsage1,
                          }
                        : m
                    ));
                    break;
                }
              } catch (e) {
                console.error('Failed to parse event:', jsonStr);
              }
            }
          }
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      updateTargetMessages(prev => prev.map(m =>
        m.id === messageId + '-assistant'
          ? { ...m, isLoading: false, error: err instanceof Error ? err.message : 'Unknown error', status: undefined }
          : m
      ));
    } finally {
      abortControllerRef.current = null;
      currentMessageIdRef.current = null;
      setIsProcessing(false);
      focusInput();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isProcessing) return;

    // Ensure session exists before sending message
    let currentSessionId = sessionId;
    if (!currentSessionId) {
      console.log('No session, creating one before sending message...');
      try {
        const response = await fetch(`${API_BASE_URL}/session`, {
          method: 'POST',
          ...getFetchOptions(),
        });
        if (response.ok) {
          const data = await response.json();
          currentSessionId = data.session_id;
          // Update the current session with the new ID
          updateCurrentSession(s => ({ ...s, id: data.session_id }));
          console.log('Session created on-demand:', data.session_id);
        }
      } catch (err) {
        console.error('Failed to create session:', err);
      }
    }

    const userMessage = inputMessage.trim();

    // If user typed /analysisname and pressed Enter, route to analysis/skill invocation
    if (userMessage.startsWith('/') && userMessage.length > 1) {
      const analysisName = userMessage.slice(1).trim();
      if (analysisName === 'source-of-truth-data') {
        setInputMessage('');
        navigate('/joinpavilion/source-of-truth-data');
        return;
      }
      const matchingAgent = agents.find(a => a.name === analysisName);
      if (matchingAgent) {
        setInputMessage('');
        handleAgentInvoke(matchingAgent.name);
        return;
      }
    }

    // Update session name on first message (when name is still "New Chat")
    if (currentSession?.name === 'New Chat') {
      updateCurrentSession(s => ({ ...s, name: generateSessionName(userMessage) }));
    }
    const messageId = Date.now().toString();

    // Add user message
    setMessages(prev => [...prev, {
      id: messageId + '-user',
      type: 'user',
      content: userMessage,
    }]);

    // Add assistant placeholder
    setMessages(prev => [...prev, {
      id: messageId + '-assistant',
      type: 'assistant',
      content: '',
      isLoading: true,
      status: 'Thinking...',
    }]);

    // Clear input immediately
    setInputMessage('');
    setIsProcessing(true);
    currentMessageIdRef.current = messageId;

    // Create abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      // Get local schemas if we have local files
      const localSchemas = await getLocalSchemasForBackend(getSchemas, localFiles);
      console.log('[CHAT] Sending request with local_schemas:', localSchemas?.length || 0, 'tables', localSchemas);

      const fetchOpts = getFetchOptionsWithBody({
        message: userMessage,
        session_id: currentSessionId,
        local_schemas: localSchemas,
      });
      const response = await fetch(`${API_BASE_URL}/chat/stream`, {
        method: 'POST',
        ...fetchOpts,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error('Failed to connect to backend');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      let accumulatedData: Record<string, unknown>[] | undefined;
      let accumulatedAnalysis = '';
      let rowCount = 0;
      let accumulatedChartConfig: ChartConfig | undefined;
      let accumulatedTokenUsage: TokenUsage | undefined;
      let accumulatedSql: string | undefined;
      let currentTableName: string | undefined; // Track table name for aggregation fallback
      let currentBackendSessionId: string | undefined; // Track backend DuckDB session ID

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data:')) {
            const jsonStr = line.startsWith('data: ') ? line.slice(6) : line.slice(5);
            if (jsonStr.trim()) {
              try {
                const event: StreamEvent = JSON.parse(jsonStr);

                switch (event.event_type) {
                  case 'status':
                    setMessages(prev => prev.map(m =>
                      m.id === messageId + '-assistant'
                        ? { ...m, status: event.payload.message || 'Processing...' }
                        : m
                    ));
                    break;
                  case 'data':
                    accumulatedData = event.payload.data;
                    rowCount = event.payload.row_count || 0;
                    break;
                  case 'analysis':
                    accumulatedAnalysis = event.payload.analysis || '';
                    break;
                  case 'iteration':
                    // Add iteration info to the message
                    if (event.payload.iteration !== undefined) {
                      const newIteration: IterationInfo = {
                        iteration: event.payload.iteration,
                        action: event.payload.action || 'unknown',
                        detail: event.payload.detail || '',
                        result: event.payload.result,
                      };
                      // Capture SQL from execute_sql actions
                      if (newIteration.action === 'execute_sql' && newIteration.detail) {
                        accumulatedSql = newIteration.detail;
                      }
                      setMessages(prev => prev.map(m =>
                        m.id === messageId + '-assistant'
                          ? {
                              ...m,
                              iterations: [...(m.iterations || []), newIteration],
                              status: `Iteration ${newIteration.iteration}: ${newIteration.action}`
                            }
                          : m
                      ));
                    }
                    break;
                  case 'progress':
                    // Handle agent-driven task progress updates
                    if (event.payload.tasks) {
                      const progressTasks = event.payload.tasks;
                      setMessages(prev => prev.map(m =>
                        m.id === messageId + '-assistant'
                          ? { ...m, progressTasks }
                          : m
                      ));
                    }
                    break;
                  case 'chart':
                    // Handle chart configuration from unified visualizer sub-agent
                    // Visualizer returns complete config with chart type and axis formatting
                    if (event.payload.chart_type && event.payload.x_column && event.payload.y_column) {
                      accumulatedChartConfig = {
                        chart_type: event.payload.chart_type,
                        x_column: event.payload.x_column,
                        y_column: event.payload.y_column,
                        title: event.payload.title,
                        x_label: event.payload.x_label,
                        y_label: event.payload.y_label,
                        // Include axis formatting from visualizer
                        x_axis: event.payload.x_axis,
                        y_axis: event.payload.y_axis,
                        colors: event.payload.colors,
                        legend: event.payload.legend,
                        data_labels: event.payload.data_labels,
                        sorting: event.payload.sorting,
                        column_order: event.payload.column_order,
                        tile_formats: event.payload.tile_formats,
                        // Interactive filters and source table for backend DuckDB queries
                        filters: event.payload.filters,
                        source_table: event.payload.source_table,
                        source_sql: event.payload.source_sql,
                        recon_metadata: event.payload.recon_metadata,
                      };
                      console.log('Chart config received:', accumulatedChartConfig, 'sorting:', event.payload.sorting);
                      // Capture for closure
                      const chartConfigForUpdate = accumulatedChartConfig;
                      // Update message immediately to show chart update
                      setMessages(prev => prev.map(m =>
                        m.id === messageId + '-assistant'
                          ? { ...m, chartConfig: chartConfigForUpdate }
                          : m
                      ));
                    }
                    break;
                  case 'tokens':
                    // Update token usage - use latest values (backend sends cumulative per-request)
                    accumulatedTokenUsage = {
                      input_tokens: event.payload.input_tokens || 0,
                      output_tokens: event.payload.output_tokens || 0,
                      total_input_tokens: event.payload.total_input_tokens || 0,
                      total_output_tokens: event.payload.total_output_tokens || 0,
                    };
                    // Capture for closure
                    const tokenUsageForUpdate = accumulatedTokenUsage;
                    // Update message with latest token usage
                    setMessages(prev => prev.map(m =>
                      m.id === messageId + '-assistant'
                        ? { ...m, tokenUsage: tokenUsageForUpdate }
                        : m
                    ));
                    break;
                  case 'fetch_data_meta':
                    // Metadata only — data stays in backend DuckDB
                    if (event.payload.table_name) {
                      currentTableName = event.payload.table_name;
                      rowCount = event.payload.row_count || 0;
                      // Track session_id for backend DuckDB queries
                      currentBackendSessionId = event.payload.session_id;
                      // Don't store data in WASM, don't call storeFetchedData
                      console.log(`[CHAT] Table '${currentTableName}' available in backend DuckDB (${rowCount} rows)`);
                    }
                    break;
                  case 'fetch_data':
                    // Store fetched data from remote database into DuckDB WASM
                    if (event.payload.table_name && event.payload.data && event.payload.metadata) {
                      const tableName = event.payload.table_name;
                      const data = event.payload.data;
                      const metadata = event.payload.metadata;
                      const warning = event.payload.warning;
                      currentTableName = tableName; // Track for aggregation fallback

                      console.log('[FETCH_DATA] Storing:', tableName, data.length, 'rows');

                      setMessages(prev => prev.map(m =>
                        m.id === messageId + '-assistant'
                          ? { ...m, status: `Storing ${data.length} rows in ${tableName}...` }
                          : m
                      ));

                      // Capture SQL from metadata (snake_case from backend, camelCase for fallback)
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const metadataAny = metadata as any;
                      const sourceQuery = metadataAny.source_query || metadataAny.sourceQuery || '';
                      if (sourceQuery) {
                        accumulatedSql = sourceQuery;
                      }

                      // Store in DuckDB WASM
                      storeFetchedData(tableName, data, {
                        source: metadataAny.source,
                        sourceQuery: sourceQuery,
                        question: metadataAny.question,
                        rowCount: metadataAny.row_count || metadataAny.rowCount,
                        truncated: metadataAny.truncated,
                        columns: metadataAny.columns,
                        description: metadataAny.description,
                      }).then(() => {
                        console.log('[FETCH_DATA] Stored successfully:', tableName);
                        // Notify backend that data is ready for exploration
                        sendDataStored(tableName);
                        // Update data and rowCount on the message
                        accumulatedData = data;
                        rowCount = data.length;

                        // If there was a truncation warning, include it in analysis
                        if (warning) {
                          accumulatedAnalysis = warning + '\n\n' + accumulatedAnalysis;
                        }
                      }).catch(err => {
                        console.error('[FETCH_DATA] Error storing:', err);
                        // Don't fail the whole request, just log it
                      });
                    }
                    break;
                  case 'explore_query':
                    // Execute exploration SQL against local DuckDB WASM
                    if (event.payload.sql && event.payload.target_table) {
                      const exploreSql = event.payload.sql;
                      const targetTable = event.payload.target_table;

                      console.log('[EXPLORE_QUERY] Executing on', targetTable, ':', exploreSql);

                      setMessages(prev => prev.map(m =>
                        m.id === messageId + '-assistant'
                          ? { ...m, status: `Exploring ${targetTable}...` }
                          : m
                      ));

                      // Execute in DuckDB-WASM
                      executeDuckDBQuery(exploreSql).then(result => {
                        if (result.success) {
                          console.log('[EXPLORE_QUERY] Success:', result.rowCount, 'rows');
                          // Update accumulated data for the message
                          accumulatedData = result.rows;
                          rowCount = result.rowCount;
                          accumulatedSql = exploreSql;
                        } else {
                          console.error('[EXPLORE_QUERY] Error:', result.error);
                          // Continue - the agent will handle the error
                        }
                      }).catch(err => {
                        console.error('[EXPLORE_QUERY] Exception:', err);
                      });
                    }
                    break;
                  case 'error':
                    setMessages(prev => prev.map(m =>
                      m.id === messageId + '-assistant'
                        ? { ...m, isLoading: false, error: event.payload.error, status: undefined }
                        : m
                    ));
                    break;
                  case 'complete':
                    // Capture all values for closure
                    const finalAnalysis = accumulatedAnalysis;
                    let finalData = accumulatedData;
                    let finalRowCount = rowCount;
                    const finalChartConfig = accumulatedChartConfig;
                    const finalTokenUsage = accumulatedTokenUsage;
                    const finalSql = accumulatedSql;
                    const finalQueryId = event.payload.query_id ? String(event.payload.query_id) : undefined;
                    const finalTableName = currentTableName;
                    const finalBackendSessionId = currentBackendSessionId;

                    // Frontend aggregation fallback: if chart requested but data is too large,
                    // aggregate by x_column in DuckDB WASM to make it chartable
                    const CHART_DATA_THRESHOLD = 500;
                    if (finalChartConfig && finalData && finalData.length > CHART_DATA_THRESHOLD && currentTableName) {
                      console.log(`[CHART_AGGREGATION] Data too large (${finalData.length} rows), aggregating by ${finalChartConfig.x_column}`);

                      // Build aggregation SQL: GROUP BY x_column, SUM y_column
                      const xCol = finalChartConfig.x_column;
                      const yCol = finalChartConfig.y_column;
                      const aggregateSql = `SELECT "${xCol}", SUM("${yCol}") as "${yCol}" FROM "${currentTableName}" GROUP BY "${xCol}" ORDER BY "${xCol}"`;

                      console.log('[CHART_AGGREGATION] SQL:', aggregateSql);

                      // Execute synchronously-ish using promise
                      executeDuckDBQuery(aggregateSql).then(result => {
                        if (result.success && result.rows) {
                          console.log(`[CHART_AGGREGATION] Success: ${result.rowCount} aggregated rows`);
                          setMessages(prev => prev.map(m =>
                            m.id === messageId + '-assistant'
                              ? {
                                  ...m,
                                  isLoading: false,
                                  status: undefined,
                                  content: finalAnalysis,
                                  data: result.rows,
                                  rowCount: result.rowCount,
                                  chartConfig: finalChartConfig,
                                  tokenUsage: finalTokenUsage,
                                  sql: finalSql,
                                  queryId: finalQueryId,
                                  tableName: finalTableName,
                                  backendSessionId: finalBackendSessionId,
                                }
                              : m
                          ));
                        } else {
                          console.error('[CHART_AGGREGATION] Failed:', result.error);
                          // Fall back to original data (may not render chart)
                          setMessages(prev => prev.map(m =>
                            m.id === messageId + '-assistant'
                              ? {
                                  ...m,
                                  isLoading: false,
                                  status: undefined,
                                  content: finalAnalysis,
                                  data: finalData,
                                  rowCount: finalRowCount,
                                  chartConfig: finalChartConfig,
                                  tokenUsage: finalTokenUsage,
                                  sql: finalSql,
                                  queryId: finalQueryId,
                                  tableName: finalTableName,
                                  backendSessionId: finalBackendSessionId,
                                }
                              : m
                          ));
                        }
                      }).catch(err => {
                        console.error('[CHART_AGGREGATION] Exception:', err);
                        // Fall back to original data
                        setMessages(prev => prev.map(m =>
                          m.id === messageId + '-assistant'
                            ? {
                                ...m,
                                isLoading: false,
                                status: undefined,
                                content: finalAnalysis,
                                data: finalData,
                                rowCount: finalRowCount,
                                chartConfig: finalChartConfig,
                                tokenUsage: finalTokenUsage,
                                sql: finalSql,
                                queryId: finalQueryId,
                                tableName: finalTableName,
                                backendSessionId: finalBackendSessionId,
                              }
                            : m
                        ));
                      });
                    } else {
                      // Normal path: data is small enough or no chart requested
                      setMessages(prev => prev.map(m =>
                        m.id === messageId + '-assistant'
                          ? {
                              ...m,
                              isLoading: false,
                              status: undefined,
                              content: finalAnalysis,
                              data: finalData,
                              rowCount: finalRowCount,
                              chartConfig: finalChartConfig,
                              tokenUsage: finalTokenUsage,
                              sql: finalSql,
                              queryId: finalQueryId,
                              tableName: finalTableName,
                              backendSessionId: finalBackendSessionId,
                            }
                          : m
                      ));
                    }
                    break;
                }
              } catch (e) {
                console.error('Failed to parse event:', jsonStr);
              }
            }
          }
        }
      }
    } catch (err) {
      // Don't show error for aborted requests (handled by escape key handler)
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setMessages(prev => prev.map(m =>
        m.id === messageId + '-assistant'
          ? { ...m, isLoading: false, error: err instanceof Error ? err.message : 'Unknown error', status: undefined }
          : m
      ));
    } finally {
      abortControllerRef.current = null;
      currentMessageIdRef.current = null;
      setIsProcessing(false);
      focusInput();
    }
  };

  // Handle saving edited SQL and re-running the analysis
  const handleSaveSql = async (messageId: string, agentItemId: string, analysisName: string) => {
    const newSql = editedSql[messageId];
    if (!newSql) return;

    try {
      const response = await fetch(`${API_BASE_URL}/analysis/${analysisName}/items/${agentItemId}`, {
        method: 'PUT',
        ...getFetchOptionsWithBody({ sql: newSql }),
      });

      if (!response.ok) {
        throw new Error('Failed to save SQL');
      }

      // Clear the edited state
      setEditedSql(prev => {
        const next = { ...prev };
        delete next[messageId];
        return next;
      });

      // Re-run the analysis to refresh all data
      handleAgentInvoke(analysisName);
    } catch (err) {
      console.error('Failed to save SQL:', err);
    }
  };

  // Handle edit visualization request (preview only, no save)
  const handleEditViz = async (messageId: string, agentItemId: string, analysisName: string) => {
    const prompt = editVizPrompt[messageId];
    if (!prompt?.trim()) return;

    setEditVizLoading(prev => ({ ...prev, [messageId]: true }));
    try {
      const response = await fetch(
        `${API_BASE_URL}/analysis/${analysisName}/items/${agentItemId}/edit-viz`,
        { method: 'POST', ...getFetchOptionsWithBody({ prompt: prompt.trim() }) }
      );
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to edit visualization');
      }
      const result = await response.json();
      setEditVizPreview(prev => ({
        ...prev,
        [messageId]: { chartConfig: result.chart_config, data: result.data },
      }));
    } catch (err) {
      console.error('Failed to edit visualization:', err);
      alert(`Edit failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setEditVizLoading(prev => ({ ...prev, [messageId]: false }));
    }
  };

  // Save the previewed visualization edit
  const handleSaveVizEdit = async (messageId: string, agentItemId: string, analysisName: string) => {
    const preview = editVizPreview[messageId];
    if (!preview) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/analysis/${analysisName}/items/${agentItemId}`,
        { method: 'PUT', ...getFetchOptionsWithBody({ chart_config: preview.chartConfig }) }
      );
      if (!response.ok) throw new Error('Failed to save visualization');

      // Update the message in place with new chart config and data
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, chartConfig: preview.chartConfig, data: preview.data } : m
      ));
      handleDiscardVizEdit(messageId);
    } catch (err) {
      console.error('Failed to save visualization:', err);
      alert('Failed to save visualization');
    }
  };

  // Discard the previewed visualization edit
  const handleDiscardVizEdit = (messageId: string) => {
    setEditVizPrompt(prev => { const next = { ...prev }; delete next[messageId]; return next; });
    setEditVizPreview(prev => { const next = { ...prev }; delete next[messageId]; return next; });
    setEditVizLoading(prev => { const next = { ...prev }; delete next[messageId]; return next; });
  };

  // Handle deleting an item from an agent
  const handleDeleteFromAgent = async (agentItemId: string, analysisName: string, messageId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/analysis/${analysisName}/items/${agentItemId}`, {
        method: 'DELETE',
        ...getFetchOptions(),
      });

      if (response.ok) {
        // Remove the message from the UI
        setMessages(prev => prev.filter(m => m.id !== messageId));
        // Refresh agents list to update item count
        fetchAgents();
      } else {
        console.error('Failed to delete analysis item');
      }
    } catch (err) {
      console.error('Error deleting analysis item:', err);
    }
  };

  // Handle deleting an entire agent
  const handleDeleteAgent = async (analysisName: string) => {
    if (!window.confirm(`Are you sure you want to delete the analysis "${analysisName}"? This will delete all saved queries and charts.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/analysis/${analysisName}`, {
        method: 'DELETE',
        ...getFetchOptions(),
      });

      if (response.ok) {
        // Close the current session tab
        handleCloseSession(activeSessionIndex, { stopPropagation: () => {} } as React.MouseEvent);
        // Refresh agents list
        fetchAgents();
      } else {
        console.error('Failed to delete analysis');
        alert('Failed to delete analysis');
      }
    } catch (err) {
      console.error('Error deleting analysis:', err);
      alert('Error deleting analysis');
    }
  };

  // Handle feedback (thumbs up/down)
  const handleFeedback = async (messageId: string, rating: 'up' | 'down') => {
    // Don't allow changing feedback once given
    if (feedbackGiven[messageId]) return;

    // Look up the query_id from the message
    const message = messages.find(m => m.id === messageId);
    const queryId = message?.queryId;

    try {
      const fetchOpts = getFetchOptionsWithBody({
        message_id: messageId,
        rating,
        session_id: sessionId,
        query_id: queryId,
      });
      await fetch(`${API_BASE_URL}/feedback`, {
        method: 'POST',
        ...fetchOpts,
      });
      // Mark feedback as given (fire and forget - don't worry about response)
      setFeedbackGiven(prev => ({ ...prev, [messageId]: rating }));
    } catch (err) {
      // Silently fail - feedback is optional
      console.error('Error submitting feedback:', err);
    }
  };

  // Handle input change with command detection
  const handleInputChange = (value: string) => {
    setInputMessage(value);
    // Determine which dropdown to show based on command (enabled for all users)
    if (value.startsWith('/')) {
      setActiveDropdown('analysis');
    } else {
      setActiveDropdown(null);
    }
  };

  // Redirect to Google sign-in if not authenticated
  if (!authLoading && !isAuthenticated) {
    const slug = company || 'anatase';
    window.location.href = `${API_BASE_URL}/api/auth/google?redirect=/${slug}/dashboard&company=${slug}`;
    return null;
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="App" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'rgba(255,255,255,0.5)' }}>Loading...</div>
      </div>
    );
  }

  const hasMessages = messages.length > 0;

  // Placeholder text - same for all users now
  const placeholderText = 'Ask about your data... (type /analysis for saved analyses)';

  return (
    <div className={`App ${!hasMessages ? 'empty-mode' : ''}`}>
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <img src="/logo.svg" alt="Anatase" className="header-logo" />
        </div>
        <div className="session-tabs-container">
          <div className="session-tabs">
          {sessions.map((session, index) => {
            const isAgentSession = session.name.startsWith('/');
            return (
            <button
              key={session.id}
              className={`session-tab ${index === activeSessionIndex ? 'active' : ''} ${isAgentSession ? 'analysis-session' : ''}`}
              onClick={() => handleSwitchSession(index)}
              title={session.name}
            >
              <span className="session-tab-name">{session.name}</span>
              {sessions.length > 1 && (
                <span
                  className="session-tab-close"
                  onClick={(e) => handleCloseSession(index, e)}
                  title="Close tab"
                >
                  ×
                </span>
              )}
            </button>
            );
          })}
          <button
            className="new-chat-button-compact"
            onClick={handleNewChat}
            disabled={isProcessing}
            title="New Chat"
          >
            +
          </button>
          </div>
          <div className="header-right">
            {/* User info and logout */}
            {user && (
              <>
                <span className="user-email">{user.email}</span>
                <button
                  className="logout-button"
                  onClick={handleLogout}
                  title="Log out"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Messages Area */}
      <main className="messages-container">
        {messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <h2>Ask about your data</h2>
            <p>Try questions like "What data do you have?" or "Show revenue for last 8 quarters"</p>

            {/* Centered input for empty state */}
            <div className="input-container-inline">
              <div className="input-wrapper">
                {/* Command Dropdowns */}
                {activeDropdown === 'analysis' && agents.length > 0 && (
                  <div className="analysis-dropdown">
                    {agents
                      .filter(a => inputMessage === '/' || a.name.toLowerCase().includes(inputMessage.slice(1).toLowerCase()))
                      .map(agent => (
                        <button
                          key={agent.id}
                          className="analysis-dropdown-item"
                          onClick={() => {
                            setInputMessage('');
                            setActiveDropdown(null);
                            handleAgentInvoke(agent.name);
                          }}
                        >
                          <span className="analysis-name">/{agent.name}</span>
                          <span className="analysis-description">{agent.display_name} ({agent.item_count} items)</span>
                        </button>
                      ))}
                    {agents.filter(a => inputMessage === '/' || a.name.toLowerCase().includes(inputMessage.slice(1).toLowerCase())).length === 0 && (
                      <div className="analysis-dropdown-empty">No analyses found</div>
                    )}
                  </div>
                )}
                <form onSubmit={handleSubmit} className="input-form">
                  <input
                    ref={emptyInputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onBlur={() => {
                      setTimeout(() => setActiveDropdown(null), 200);
                    }}
                    placeholder={placeholderText}
                    className="chat-input"
                    disabled={isProcessing}
                  />
                  <button type="submit" className="send-button" disabled={isProcessing || !inputMessage.trim()}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                    </svg>
                  </button>
                </form>
              </div>
            </div>
          </div>
        ) : (
          <div className="messages-list">
            {/* Agent header with delete button */}
            {currentSession?.analysisName && (
              <div className="analysis-header">
                <span className="analysis-header-name">
                  {agents.find(a => a.name === currentSession.analysisName)?.display_name || currentSession.analysisName}
                </span>
                <button
                  className="delete-analysis-btn"
                  onClick={() => handleDeleteAgent(currentSession.analysisName!)}
                  title="Delete this analysis"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    <line x1="10" y1="11" x2="10" y2="17"/>
                    <line x1="14" y1="11" x2="14" y2="17"/>
                  </svg>
                  Delete Analysis
                </button>
              </div>
            )}
            {messages.map((message) => (
              <div key={message.id} className={`message ${message.type}`}>
                {message.type === 'user' ? (
                  <div className="user-message">
                    <div className="message-icon user-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                    </div>
                    <div className="message-content">{message.content}</div>
                  </div>
                ) : (
                  <div className="assistant-message">
                    {/* Hide logo for agent item messages */}
                    {!message.agentItemId && (
                    <div className="message-icon assistant-icon">
                      <img src="/logo.svg" alt="Anatase" width="16" height="16" />
                    </div>
                    )}
                    <div className="message-body">
                      {message.isLoading ? (
                        <div className="loading-state">
                          <div className="loading-header">
                            <span>{message.status}</span>
                            <span className="esc-hint">esc to cancel</span>
                          </div>
                          {message.iterations && message.iterations.length > 0 && (
                            <div className="iterations-list">
                              {message.iterations.map((iter, idx) => (
                                <div key={idx} className={`iteration-item ${iter.action}`}>
                                  <span className="iteration-number">#{iter.iteration}</span>
                                  <span className="iteration-action">{iter.action}</span>
                                  {iter.action === 'execute_sql' && (
                                    <code className="iteration-sql">{iter.detail.length > 80 ? iter.detail.slice(0, 80) + '...' : iter.detail}</code>
                                  )}
                                  {(iter.action === 'explore_data' || iter.action === 'fetch_data') && iter.detail && (
                                    <span className="iteration-intent">{iter.detail}</span>
                                  )}
                                  {iter.action === 'fetch_data_result' && (
                                    <span className="iteration-result">completed</span>
                                  )}
                                  {iter.action === 'visualizer_result' && iter.detail && (
                                    <span className="iteration-intent">{iter.detail}</span>
                                  )}
                                  {iter.action !== 'explore_data' && iter.action !== 'fetch_data' && iter.action !== 'fetch_data_result' && iter.action !== 'visualizer' && iter.action !== 'visualizer_result' && iter.result && <span className="iteration-result">{iter.result}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          {message.progressTasks && message.progressTasks.length > 0 && (
                            <div className="progress-tasks">
                              {message.progressTasks.map((task) => (
                                <div key={task.id} className={`progress-task ${task.status}`}>
                                  <span className="progress-task-icon">
                                    {task.status === 'completed' ? '✓' : task.status === 'in_progress' ? '◌' : '○'}
                                  </span>
                                  <span className="progress-task-description">{task.description}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : message.error ? (
                        <div className="error-message">{message.error}</div>
                      ) : (
                        <>
                          {/* Tabbed Results Interface */}
                          {(message.data || message.chartConfig || message.content) && (
                            <div className="results-tabs-container">
                              {/* Tab Headers */}
                              <div className="results-tabs">
                                {message.chartConfig && message.data && message.data.length > 0 && (
                                  <button
                                    className={`results-tab ${(activeTab[message.id] || 'chart') === 'chart' ? 'active' : ''}`}
                                    onClick={() => setActiveTab(prev => ({ ...prev, [message.id]: 'chart' }))}
                                  >
                                    Chart
                                  </button>
                                )}
                                {message.data && message.data.length > 0 && (
                                  <button
                                    className={`results-tab ${activeTab[message.id] === 'data' ? 'active' : ''}`}
                                    onClick={() => setActiveTab(prev => ({ ...prev, [message.id]: 'data' }))}
                                  >
                                    Data ({message.rowCount})
                                  </button>
                                )}
                                {message.sql && message.data && message.data.length > 0 && (
                                  <button
                                    className={`results-tab ${activeTab[message.id] === 'sql' ? 'active' : ''}`}
                                    onClick={() => setActiveTab(prev => ({ ...prev, [message.id]: 'sql' }))}
                                  >
                                    SQL
                                  </button>
                                )}
                                {message.content && (
                                  <button
                                    className={`results-tab ${activeTab[message.id] === 'analysis' || (!message.chartConfig && !activeTab[message.id]) ? 'active' : ''}`}
                                    onClick={() => setActiveTab(prev => ({ ...prev, [message.id]: 'analysis' }))}
                                  >
                                    Analysis
                                  </button>
                                )}
                                {/* Edit Chart / Save / Delete buttons for analysis items */}
                                {message.agentItemId && currentSession?.analysisName && message.chartConfig && (
                                  <button
                                    className="edit-viz-btn"
                                    onClick={() => {
                                      if (editVizPrompt[message.id] !== undefined) {
                                        handleDiscardVizEdit(message.id);
                                      } else {
                                        setEditVizPrompt(prev => ({ ...prev, [message.id]: '' }));
                                      }
                                    }}
                                    title="Edit Visualization"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                    </svg>
                                    Edit Chart
                                  </button>
                                )}
                                {/* Save to Analysis / Delete from Analysis button */}
                                {message.data && message.data.length > 0 && message.chartConfig && (
                                  message.agentItemId && currentSession?.analysisName ? (
                                    // Delete from Analysis button for agent items
                                    <button
                                      className="delete-from-analysis-btn"
                                      onClick={() => handleDeleteFromAgent(message.agentItemId!, currentSession.analysisName!, message.id)}
                                      title="Delete from Analysis"
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="3 6 5 6 21 6"/>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                        <line x1="10" y1="11" x2="10" y2="17"/>
                                        <line x1="14" y1="11" x2="14" y2="17"/>
                                      </svg>
                                      Delete from Analysis
                                    </button>
                                  ) : (
                                    // Save to Analysis button for regular queries
                                    <button
                                      className="save-to-analysis-btn"
                                      onClick={() => {
                                        // Find SQL: message.sql > chartConfig.source_sql > previous messages
                                        let sqlToSave = message.sql || message.chartConfig?.source_sql;
                                        if (!sqlToSave) {
                                          for (let i = messages.length - 1; i >= 0; i--) {
                                            if (messages[i].sql) {
                                              sqlToSave = messages[i].sql;
                                              break;
                                            }
                                          }
                                        }
                                        setSaveToAnalysisData({
                                          messageId: message.id,
                                          sql: sqlToSave || '',
                                          chartConfig: message.chartConfig || null,
                                          suggestedName: message.chartConfig?.title || 'Query Result',
                                        });
                                        setShowAgentModal(true);
                                      }}
                                      title="Save to Analysis"
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                        <polyline points="17 21 17 13 7 13 7 21"/>
                                        <polyline points="7 3 7 8 15 8"/>
                                      </svg>
                                      Save to Analysis
                                    </button>
                                  )
                                )}
                                {/* Universal CSV download — hides itself if no source is resolvable */}
                                <DownloadButton
                                  data={message.data}
                                  chartConfig={message.chartConfig}
                                  backendSessionId={message.backendSessionId}
                                  title={message.chartConfig?.title || message.content}
                                />
                              </div>

                              {/* Edit Visualization Prompt Bar */}
                              {editVizPrompt[message.id] !== undefined && message.agentItemId && currentSession?.analysisName && (
                                <div className="edit-viz-bar">
                                  <input
                                    type="text"
                                    className="edit-viz-input"
                                    placeholder="e.g., change to bar chart, add data labels, sort descending..."
                                    value={editVizPrompt[message.id] || ''}
                                    onChange={(e) => setEditVizPrompt(prev => ({ ...prev, [message.id]: e.target.value }))}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !editVizLoading[message.id]) {
                                        handleEditViz(message.id, message.agentItemId!, currentSession!.analysisName!);
                                      }
                                    }}
                                    disabled={editVizLoading[message.id]}
                                    autoFocus
                                  />
                                  <button
                                    className="edit-viz-submit"
                                    onClick={() => handleEditViz(message.id, message.agentItemId!, currentSession!.analysisName!)}
                                    disabled={editVizLoading[message.id] || !editVizPrompt[message.id]?.trim()}
                                  >
                                    {editVizLoading[message.id] ? 'Generating...' : 'Preview'}
                                  </button>
                                  <button className="edit-viz-cancel" onClick={() => handleDiscardVizEdit(message.id)}>
                                    Cancel
                                  </button>
                                </div>
                              )}

                              {/* Edit Visualization Preview */}
                              {editVizPreview[message.id] && (
                                <div className="edit-viz-preview">
                                  <div className="edit-viz-preview-header">
                                    <span>Preview</span>
                                    <div className="edit-viz-preview-actions">
                                      <button className="edit-viz-save" onClick={() => handleSaveVizEdit(message.id, message.agentItemId!, currentSession!.analysisName!)}>
                                        Save
                                      </button>
                                      <button className="edit-viz-discard" onClick={() => handleDiscardVizEdit(message.id)}>
                                        Discard
                                      </button>
                                    </div>
                                  </div>
                                  <div className="chart-result">
                                    <FilterableChart config={editVizPreview[message.id]!.chartConfig} data={editVizPreview[message.id]!.data} />
                                  </div>
                                </div>
                              )}

                              {/* Tab Content */}
                              <div className="results-tab-content">
                                {/* Chart Tab */}
                                {(activeTab[message.id] || (message.chartConfig ? 'chart' : 'analysis')) === 'chart' && message.chartConfig && ((message.data && message.data.length > 0) || message.chartConfig.source_table || message.chartConfig.source_sql) && (
                                  <div className="chart-result">
                                    <FilterableChart config={message.chartConfig} data={message.data || []} tableName={message.tableName} baseSql={message.sql} />
                                  </div>
                                )}

                                {/* Data Tab */}
                                {activeTab[message.id] === 'data' && message.data && message.data.length > 0 && (
                                  <div className="data-result">
                                    <div className="table-wrapper">
                                      <table className="data-table">
                                        <thead>
                                          <tr>
                                            {Object.keys(message.data[0]).map((key) => (
                                              <th key={key}>{key}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {message.data.slice(0, 50).map((row, i) => (
                                            <tr key={i}>
                                              {Object.values(row).map((val, j) => {
                                                const isNum = val !== null && typeof val === 'number';
                                                return (
                                                  <td key={j} className={isNum ? 'numeric' : ''}>
                                                    {val === null ? '-' : isNum ? val.toLocaleString('en-US', { maximumFractionDigits: 2 }) : String(val)}
                                                  </td>
                                                );
                                              })}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                      {message.data.length > 50 && (
                                        <p className="table-note">
                                          Showing 50 of {message.data.length} rows
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* SQL Tab */}
                                {activeTab[message.id] === 'sql' && message.sql && (
                                  <div className="sql-result">
                                    {message.agentItemId && currentSession?.analysisName ? (
                                      <>
                                        <textarea
                                          className="sql-editor"
                                          value={editedSql[message.id] ?? message.sql}
                                          onChange={(e) => setEditedSql(prev => ({ ...prev, [message.id]: e.target.value }))}
                                          spellCheck={false}
                                        />
                                        {(editedSql[message.id] !== undefined && editedSql[message.id] !== message.sql) && (
                                          <div className="sql-editor-actions">
                                            <button
                                              className="sql-save-btn"
                                              onClick={() => handleSaveSql(message.id, message.agentItemId!, currentSession.analysisName!)}
                                            >
                                              Run
                                            </button>
                                            <button
                                              className="sql-revert-btn"
                                              onClick={() => setEditedSql(prev => {
                                                const next = { ...prev };
                                                delete next[message.id];
                                                return next;
                                              })}
                                            >
                                              Revert
                                            </button>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <pre className="sql-code">{message.sql}</pre>
                                    )}
                                  </div>
                                )}

                                {/* Analysis Tab */}
                                {(activeTab[message.id] === 'analysis' || (!message.chartConfig && !activeTab[message.id])) && message.content && (
                                  <div className="analysis-content">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                      {message.content}
                                    </ReactMarkdown>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          {message.tokenUsage && (
                            <div className="token-usage">
                              <span className="token-label">Tokens:</span>
                              <span className="token-count">{message.tokenUsage.input_tokens.toLocaleString()} in</span>
                              <span className="token-separator">·</span>
                              <span className="token-count">{message.tokenUsage.output_tokens.toLocaleString()} out</span>
                              <span className="token-separator">|</span>
                              <span className="token-total">Session: {message.tokenUsage.total_input_tokens.toLocaleString()} in · {message.tokenUsage.total_output_tokens.toLocaleString()} out</span>
                            </div>
                          )}
                          {/* Feedback buttons - only show for non-agent-item messages */}
                          {!message.agentItemId && (
                          <div className="feedback-buttons">
                            <button
                              className={`feedback-btn thumbs-up ${feedbackGiven[message.id] === 'up' ? 'selected' : ''} ${feedbackGiven[message.id] ? 'disabled' : ''}`}
                              onClick={() => {
                                handleFeedback(message.id, 'up');
                              }}
                              disabled={!!feedbackGiven[message.id]}
                              title="Good response"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                              </svg>
                            </button>
                            <button
                              className={`feedback-btn thumbs-down ${feedbackGiven[message.id] === 'down' ? 'selected' : ''} ${feedbackGiven[message.id] ? 'disabled' : ''}`}
                              onClick={() => {
                                handleFeedback(message.id, 'down');
                              }}
                              disabled={!!feedbackGiven[message.id]}
                              title="Poor response"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
                              </svg>
                            </button>
                          </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </main>

      {/* Input Area - Fixed at Bottom */}
      <footer className="input-container">
        <div className="input-wrapper">
          {/* Command Dropdowns */}
          {activeDropdown === 'analysis' && agents.length > 0 && (
            <div className="analysis-dropdown">
              {agents
                .filter(a => inputMessage === '/' || a.name.toLowerCase().includes(inputMessage.slice(1).toLowerCase()))
                .map(agent => (
                  <button
                    key={agent.id}
                    className="analysis-dropdown-item"
                    onClick={() => {
                      setInputMessage('');
                      setActiveDropdown(null);
                      handleAgentInvoke(agent.name);
                    }}
                  >
                    <span className="analysis-name">/{agent.name}</span>
                    <span className="analysis-description">{agent.display_name} ({agent.item_count} items)</span>
                  </button>
                ))}
              {agents.filter(a => inputMessage === '/' || a.name.toLowerCase().includes(inputMessage.slice(1).toLowerCase())).length === 0 && (
                <div className="analysis-dropdown-empty">No analyses found</div>
              )}
            </div>
          )}
          <form onSubmit={handleSubmit} className="input-form">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={() => {
                // Delay hiding to allow click on dropdown
                setTimeout(() => setActiveDropdown(null), 200);
              }}
              placeholder={placeholderText}
              className="chat-input"
              disabled={isProcessing}
            />
            <button type="submit" className="send-button" disabled={isProcessing || !inputMessage.trim()}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
              </svg>
            </button>
          </form>
        </div>
      </footer>

      {/* Save to Analysis Modal */}
      {showAnalysisModal && saveToAnalysisData && (
        <SaveToAnalysisModal
          data={saveToAnalysisData}
          agents={agents}
          onClose={() => {
            setShowAgentModal(false);
            setSaveToAnalysisData(null);
          }}
          onSave={async (analysisName, isNew, itemName) => {
            try {
              let targetAgentName = analysisName;

              // Create new analysis if needed
              if (isNew) {
                const fetchOpts = getFetchOptionsWithBody({
                  name: analysisName.toLowerCase().replace(/\s+/g, '-'),
                  display_name: analysisName,
                });
                const createRes = await fetch(`${API_BASE_URL}/analysis`, {
                  method: 'POST',
                  ...fetchOpts,
                });
                if (!createRes.ok) {
                  throw new Error('Failed to create analysis');
                }
                const newAgent = await createRes.json();
                targetAgentName = newAgent.name;
              }

              // Add item to agent
              const fetchOpts = getFetchOptionsWithBody({
                name: itemName,
                sql: saveToAnalysisData.sql,
                chart_config: saveToAnalysisData.chartConfig,
              });
              const itemRes = await fetch(`${API_BASE_URL}/analysis/${targetAgentName}/items`, {
                method: 'POST',
                ...fetchOpts,
              });
              if (!itemRes.ok) {
                throw new Error('Failed to add item to analysis');
              }

              // Refresh agents list
              fetchAgents();
              setShowAgentModal(false);
              setSaveToAnalysisData(null);
            } catch (err) {
              console.error('Failed to save to analysis:', err);
              alert('Failed to save to analysis: ' + (err instanceof Error ? err.message : 'Unknown error'));
            }
          }}
        />
      )}
    </div>
  );
}

// Save to Analysis Modal Component
interface SaveToAnalysisModalProps {
  data: SaveToAnalysisData;
  agents: AgentSummary[];
  onClose: () => void;
  onSave: (analysisName: string, isNew: boolean, itemName: string) => void;
}

function SaveToAnalysisModal({ data, agents, onClose, onSave }: SaveToAnalysisModalProps) {
  const [mode, setMode] = useState<'new' | 'existing'>(agents.length === 0 ? 'new' : 'existing');
  const [newAgentName, setNewAgentName] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(agents[0]?.name || '');
  const [itemName, setItemName] = useState(data.suggestedName);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h3>Save to Analysis</h3>


        <div className="modal-section">
          <label className="radio-label">
            <input
              type="radio"
              checked={mode === 'new'}
              onChange={() => setMode('new')}
            />
            Create new analysis
          </label>
          {mode === 'new' && (
            <input
              type="text"
              value={newAgentName}
              onChange={e => setNewAgentName(e.target.value)}
              placeholder="Analysis name (e.g., Weekly Report)"
              className="modal-input"
              autoFocus
            />
          )}
        </div>

        {agents.length > 0 && (
          <div className="modal-section">
            <label className="radio-label">
              <input
                type="radio"
                checked={mode === 'existing'}
                onChange={() => setMode('existing')}
              />
              Add to existing analysis
            </label>
            {mode === 'existing' && (
              <select
                value={selectedAgent}
                onChange={e => setSelectedAgent(e.target.value)}
                className="modal-select"
              >
                {agents.map(agent => (
                  <option key={agent.id} value={agent.name}>
                    {agent.display_name} ({agent.item_count} items)
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="modal-section">
          <label className="modal-label">Item name:</label>
          <input
            type="text"
            value={itemName}
            onChange={e => setItemName(e.target.value)}
            placeholder="Name for this query/chart"
            className="modal-input"
          />
        </div>

        <div className="modal-actions">
          <button className="modal-btn cancel" onClick={onClose}>Cancel</button>
          <button
            className="modal-btn save"
            onClick={() => {
              const analysisName = mode === 'new' ? newAgentName : selectedAgent;
              if (!analysisName.trim() || !itemName.trim()) {
                alert('Please fill in all fields');
                return;
              }
              onSave(analysisName, mode === 'new', itemName);
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatPage;
